// V8.1 · Prévia detalhada de títulos faltantes/divergentes — geração nunca é cega.
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, CheckCircle2, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const KIND = {
  missing: { label: 'Faltante', cls: 'bg-amber-100 text-amber-800' },
  divergent: { label: 'Divergente', cls: 'bg-orange-100 text-orange-800' },
  existing: { label: 'Já existe', cls: 'bg-slate-100 text-slate-600' },
  locked: { label: 'Pago/protegido', cls: 'bg-emerald-100 text-emerald-800' },
  ignored: { label: 'Ignorado', cls: 'bg-slate-100 text-slate-400' },
};

const money = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function ReconcilePreviewDialog({ open, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({}); // {process_id: Set(keys)}
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/enrollment/titles/reconcile-detail');
      setData(data);
      const next = {};
      (data.processes || []).forEach((p) => {
        const keys = (p.items || []).filter((i) => i.kind === 'missing').map((i) => i.key);
        if (keys.length) next[p.process_id] = new Set(keys);
      });
      setSel(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao carregar prévia');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const toggle = (pid, key) => setSel((p) => {
    const s = new Set(p[pid] || []);
    if (s.has(key)) s.delete(key); else s.add(key);
    return { ...p, [pid]: s };
  });

  const payload = () => Object.entries(sel)
    .filter(([, s]) => s.size > 0)
    .map(([process_id, s]) => ({ process_id, keys: Array.from(s) }));

  const totalSel = payload().reduce((a, i) => a + i.keys.length, 0);

  const generate = async () => {
    if (totalSel === 0) { toast.error('Selecione ao menos um item'); return; }
    setBusy('gen');
    try {
      const { data } = await api.post('/enrollment/titles/reconcile-selected', { items: payload(), dry_run: false });
      toast.success(`${data.created} criados · ${data.updated} corrigidos · ${data.skipped} ignorados`);
      await load(); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao gerar');
    } finally { setBusy(''); }
  };

  const ignore = async () => {
    if (totalSel === 0) { toast.error('Selecione os itens a ignorar'); return; }
    if (!reason.trim()) { toast.error('Informe a justificativa'); return; }
    setBusy('ign');
    try {
      for (const it of payload()) {
        await api.post('/enrollment/titles/reconcile-ignore', { ...it, reason });
      }
      toast.success('Itens marcados como não aplicáveis');
      setReason(''); await load(); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao ignorar');
    } finally { setBusy(''); }
  };

  const repair = async () => {
    setBusy('rep');
    try {
      const dry = await api.post('/enrollment/titles/repair-plan-installments', { dry_run: true });
      const t = dry.data.totals;
      if (t.titles_created === 0 && t.items_fixed === 0) { toast.success('Nenhuma parcela faltando — planos sincronizados'); return; }
      const ok = window.confirm(`Reparar ${t.items_fixed} item(ns) de plano e criar ${t.titles_created} parcela(s) faltante(s)? Nada é duplicado e títulos pagos não são alterados.`);
      if (!ok) return;
      const { data } = await api.post('/enrollment/titles/repair-plan-installments', { dry_run: false });
      toast.success(`${data.totals.titles_created} parcelas criadas · ${data.totals.titles_updated || 0} atualizadas`);
      await load(); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no reparo');
    } finally { setBusy(''); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-h-[88vh] max-w-5xl overflow-auto" data-testid="reconcile-preview-dialog">
        <DialogHeader>
          <DialogTitle>Prévia dos títulos de matrícula/rematrícula</DialogTitle>
          <DialogDescription>
            Nada é gerado sem sua confirmação. Selecione item por item; títulos pagos nunca são alterados.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="flex items-center gap-2 py-8 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Calculando prévia…</div>}

        {!loading && data && (
          <>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-5" data-testid="reconcile-totals">
              {['missing', 'divergent', 'existing', 'locked', 'ignored'].map((k) => (
                <div key={k} className="rounded-xl border p-3">
                  <p className="text-[11px] uppercase text-slate-400">{KIND[k].label}</p>
                  <p className="text-xl font-bold">{data.totals?.[k] ?? 0}</p>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {(data.processes || []).filter((p) => p.needs_action || (p.items || []).length).map((p) => (
                <div key={p.process_id} className="rounded-xl border p-3" data-testid={`reconcile-proc-${p.process_id}`}>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold">{p.student_name} · {p.class_name || 'sem turma'} · {p.year}</p>
                      <p className="text-[11px] text-slate-500">
                        Plano: {p.plan_label || '—'} · previstas {p.expected_installments} · existentes {p.existing_titles} · faltantes {p.missing} · divergentes {p.divergent}
                      </p>
                    </div>
                    {p.error && <Badge className="bg-rose-100 text-rose-800">{p.error}</Badge>}
                  </div>
                  <table className="w-full text-[12px]">
                    <thead className="text-[10px] uppercase text-slate-400">
                      <tr>
                        <th className="w-8" /><th className="text-left">Parcela</th><th className="text-left">Categoria</th>
                        <th className="text-left">Competência</th><th className="text-left">Vencimento</th>
                        <th className="text-left">Valor</th><th className="text-left">Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(p.items || []).map((i) => (
                        <tr key={i.key} className="border-t border-slate-50" data-testid={`reconcile-item-${p.process_id}-${i.key}`}>
                          <td>
                            {(i.kind === 'missing' || i.kind === 'divergent') ? (
                              <input type="checkbox" checked={!!sel[p.process_id]?.has(i.key)}
                                onChange={() => toggle(p.process_id, i.key)}
                                data-testid={`reconcile-check-${p.process_id}-${i.key}`} />
                            ) : <EyeOff className="h-3 w-3 text-slate-300" />}
                          </td>
                          <td>{i.installment}</td>
                          <td>{i.category_id}</td>
                          <td>{i.competence}</td>
                          <td>{i.due_date}</td>
                          <td>{money(i.amount_final)}</td>
                          <td>
                            <Badge className={KIND[i.kind]?.cls}>{KIND[i.kind]?.label}</Badge>
                            {i.kind === 'divergent' && i.changed_fields && (
                              <div className="mt-1 space-y-0.5 text-[10px] text-orange-700" data-testid={`reconcile-diff-${i.key}`}>
                                {Object.entries(i.changed_fields).slice(0, 4).map(([f, v]) => (
                                  <p key={f}>{f}: <s>{String(v.before ?? '—')}</s> → <strong>{String(v.after ?? '—')}</strong></p>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {(data.processes || []).length === 0 && (
                <p className="flex items-center gap-2 py-6 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Nenhum processo com plano pendente.</p>
              )}
            </div>
          </>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Input placeholder="Justificativa (para ignorar itens)" value={reason}
            onChange={(e) => setReason(e.target.value)} className="sm:w-72" data-testid="reconcile-ignore-reason" />
          <Button variant="outline" onClick={repair} disabled={busy === 'rep'} data-testid="reconcile-repair-btn">
            {busy === 'rep' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reparar parcelas do plano'}
          </Button>
          <Button variant="outline" onClick={ignore} disabled={busy === 'ign'} data-testid="reconcile-ignore-btn">
            <AlertTriangle className="mr-1.5 h-4 w-4" /> Marcar como não aplicável ({totalSel})
          </Button>
          <Button onClick={generate} disabled={busy === 'gen' || totalSel === 0} data-testid="reconcile-generate-btn">
            {busy === 'gen' ? <Loader2 className="h-4 w-4 animate-spin" /> : `Gerar selecionados (${totalSel})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
